import React, { useState, useRef, useEffect } from 'react';
import { Bell } from 'react-feather';
import { useNotifications, Notification } from '../../context/NotificationContext';
import { useDashboardNavigation, notificationTypeToSection, DashboardSection } from '../../context/DashboardNavigationContext';
import NotificationDropdown from './NotificationDropdown';

export const NotificationBell: React.FC = () => {
  const { unreadCount, markAsRead } = useNotifications();
  const { navigateToSection } = useDashboardNavigation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleBellClick = () => {
    setIsOpen(!isOpen);
  };

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read
    if (!notification.isRead) {
      await markAsRead(notification.id);
    }

    // Close dropdown
    setIsOpen(false);

    // Navigate to appropriate section based on notification type
    const section = notificationTypeToSection[notification.type];
    if (section) {
      navigateToSection(section as DashboardSection, notification.entityId || undefined);
    }
  };

  // Format badge count (cap at 99+)
  const badgeText = unreadCount > 99 ? '99+' : unreadCount.toString();

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        onClick={handleBellClick}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '8px',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          minHeight: '44px',
          minWidth: '44px',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#f1f5f9';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <Bell size={20} color="#475569" />
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: '4px',
              right: '4px',
              backgroundColor: '#ef4444',
              color: '#fff',
              fontSize: '12px',
              fontWeight: '600',
              minWidth: '18px',
              height: '18px',
              borderRadius: '9px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 4px',
            }}
          >
            {badgeText}
          </span>
        )}
      </button>

      {isOpen && (
        <NotificationDropdown
          onClose={() => setIsOpen(false)}
          onNotificationClick={handleNotificationClick}
        />
      )}
    </div>
  );
};

export default NotificationBell;
